import { useEffect, useId, useRef, type ReactNode } from "react"
import { X } from "lucide-react"

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

/**
 * Right-side modal dialog. Accessibility:
 *  - role="dialog" + aria-modal + aria-labelledby
 *  - Escape closes
 *  - Focus moves into the panel on open, restored to the previously-focused
 *    element on close. Not a strict focus trap (would need bespoke code or a
 *    dependency); for internal admin tools the open-focus + Escape combo is
 *    the high-value fraction.
 */
export function Drawer({ open, onClose, title, children }: DrawerProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    const id = window.setTimeout(() => panelRef.current?.focus(), 0)
    return () => {
      document.removeEventListener("keydown", onKey)
      window.clearTimeout(id)
      restoreFocusRef.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  // z-50 so the panel sits above the sticky app header (typically z-30); the
  // header's backdrop-filter creates its own stacking context, so without a
  // gap of at least one z-tier the header can render *on top of* the panel.
  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        aria-label="Close dialog"
        className="flex-1 bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        // bg-th-bg matches the page background, so in light mode the panel
        // edge is invisible. The inner tint + heavier border give it a
        // distinct surface against both light and dark page backgrounds.
        className="flex h-full w-full max-w-md flex-col border-l-2 border-th-glass/[0.18] bg-th-bg shadow-2xl animate-fade-in focus:outline-none"
        style={{ boxShadow: "-12px 0 32px -8px rgb(0 0 0 / 0.18)" }}
      >
        <header className="flex items-center justify-between border-b border-th-glass/[0.08] bg-th-glass/[0.04] px-5 py-3">
          <h2 id={titleId} className="font-heading text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-th-fg/50 hover:bg-th-glass/[0.06] hover:text-th-fg" aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  )
}
